import axios from "axios";
import { addOp, allOps, removeOp, updateOp } from "@/lib/offlineQueue";
import { idb } from "@/lib/offlineDb";
import { SYNC, setSyncState } from "@/lib/syncState";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// After sync, temp ids (op_...) still held by open screens are swapped for the real server ids.
api.interceptors.request.use((cfg) => {
  // iOS/WebKit may answer GETs from its HTTP cache (even via an old service worker) -> force revalidation.
  if ((cfg.method || "get").toLowerCase() === "get" && cfg.responseType !== "blob") {
    cfg.headers = cfg.headers || {};
    cfg.headers["Cache-Control"] = "no-cache";
    cfg.headers["Pragma"] = "no-cache";
  }
  if (typeof cfg.url === "string" && cfg.url.includes("op_")) {
    let map = {};
    try { map = JSON.parse(localStorage.getItem("offline_id_map") || "{}"); } catch (_) {}
    cfg.url = cfg.url.replace(/op_[a-z0-9]+_[a-f0-9]+/g, (t) => map[t] || t);
  }
  return cfg;
});

// --- Silent session refresh: on 401 try /auth/refresh once, then retry the request ---
let refreshPromise = null;
function runRefresh() {
  if (!refreshPromise) {
    // Raw axios (no interceptors) to avoid recursion.
    refreshPromise = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export function formatApiError(detail) {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---------------- Offline-first overlay + write queue ----------------
function uid() {
  return `op_${Date.now().toString(36)}_${Math.random().toString(16).slice(2, 8)}`;
}
function normPath(url) {
  return (url || "").replace(API, "").split("?")[0];
}
function safeBody(op) {
  try { return JSON.parse(op.body || "{}"); } catch (_) { return {}; }
}
// Pending edits must never mask the server's current assignee (assignment is server-side only).
function overlayBody(op) {
  const { assignee_id, assignee_name, ...rest } = safeBody(op); // eslint-disable-line no-unused-vars
  return rest;
}

// Writes we can persist offline, mapped to entity + parent for the optimistic overlay.
function classifyWrite(method, url) {
  const p = normPath(url); const m = (method || "").toLowerCase(); let mm;
  // --- Facility inspection module (flat, workspace-scoped) ---
  if (p.startsWith("/inspections")) {
    if (m === "post") {
      if (p === "/inspections") return { op: "create", type: "inspections", parent: null };
      if (p === "/inspections/assets") return { op: "create", type: "insp_assets", parent: null };
      if (p === "/inspections/categories") return { op: "create", type: "insp_categories", parent: null };
      if (p === "/inspections/measures") return { op: "create", type: "insp_measures", parent: null };
      // complete / duplicate / create-followup / (de)activate / seed / documents-upload -> replay only
      return { op: "create", type: "__insp_replay__", parent: null };
    }
    if (m === "put") {
      if ((mm = p.match(/^\/inspections\/measures\/([^/]+)$/))) return { op: "update", type: "insp_measures", id: mm[1] };
      if ((mm = p.match(/^\/inspections\/assets\/([^/]+)$/))) return { op: "update", type: "insp_assets", id: mm[1] };
      if ((mm = p.match(/^\/inspections\/categories\/([^/]+)$/))) return { op: "update", type: "insp_categories", id: mm[1] };
      if (p === "/inspections/settings") return { op: "update", type: "__insp_replay__" };
      if ((mm = p.match(/^\/inspections\/([^/]+)$/))) return { op: "update", type: "inspections", id: mm[1] };
    }
    if (m === "delete") {
      if ((mm = p.match(/^\/inspections\/measures\/([^/]+)$/))) return { op: "delete", type: "insp_measures", id: mm[1] };
      if ((mm = p.match(/^\/inspections\/assets\/([^/]+)$/))) return { op: "delete", type: "insp_assets", id: mm[1] };
      if ((mm = p.match(/^\/inspections\/categories\/([^/]+)$/))) return { op: "delete", type: "insp_categories", id: mm[1] };
      if ((mm = p.match(/^\/inspections\/documents\/([^/]+)$/))) return { op: "delete", type: "__insp_replay__", id: mm[1] };
      if ((mm = p.match(/^\/inspections\/([^/]+)$/))) return { op: "delete", type: "inspections", id: mm[1] };
    }
    return null;
  }
  if (m === "post") {
    if ((mm = p.match(/^\/positions\/([^/]+)\/(tickets|defects|comments|quantities|files)$/))) return { op: "create", type: mm[2], parent: mm[1] };
    if ((mm = p.match(/^\/defects\/([^/]+)\/photos$/))) return { op: "create", type: "photos", parent: mm[1] };
    if (p === "/positions") return { op: "create", type: "positions", parent: null };
    if (p === "/media" || p === "/photos") return { op: "create", type: "__media__", parent: null };
  } else if (m === "put") {
    if ((mm = p.match(/^\/(tickets|defects|positions)\/([^/]+)$/))) return { op: "update", type: mm[1], id: mm[2] };
  } else if (m === "delete") {
    if ((mm = p.match(/^\/(tickets|defects|quantities|files|positions)\/([^/]+)$/))) return { op: "delete", type: mm[1], id: mm[2] };
  }
  return null;
}
// Overlayable list GETs -> entity + parent.
function classifyList(url) {
  const p = normPath(url); let mm;
  if ((mm = p.match(/^\/positions\/([^/]+)\/(tickets|defects|comments|quantities|files)$/))) return { type: mm[2], parent: mm[1] };
  if ((mm = p.match(/^\/defects\/([^/]+)\/photos$/))) return { type: "photos", parent: mm[1] };
  if ((mm = p.match(/^\/plans\/([^/]+)\/positions$/))) return { type: "positions", parent: mm[1] };
  // --- Facility inspection module: flat cacheable lists ---
  if (p === "/inspections") return { type: "inspections", parent: null };
  if (p === "/inspections/assets") return { type: "insp_assets", parent: null };
  if (p === "/inspections/categories") return { type: "insp_categories", parent: null };
  if (p === "/inspections/records") return { type: "insp_records", parent: null };
  if (p === "/inspections/measures") return { type: "insp_measures", parent: null };
  return null;
}

// iOS/WebKit can't reliably keep Blobs in IndexedDB: store file bytes as ArrayBuffer, rebuild a File on replay.
async function serializeFields(fields) {
  const out = [];
  for (const [k, v] of fields) {
    if (v && typeof v !== "string" && typeof v.arrayBuffer === "function") {
      out.push([k, { __file: true, buf: await v.arrayBuffer(), name: v.name || "foto.jpg", type: v.type || "image/jpeg" }]);
    } else out.push([k, v]);
  }
  return out;
}
export function reviveField(v) {
  return v && v.__file ? new File([v.buf], v.name, { type: v.type }) : v;
}
const isImageField = (v) => v && typeof v !== "string" && (v.type || "").startsWith("image/");

function buildCreateItem(op) {
  const base = { __pending: true, id: op.entityId, created_at: new Date(op.createdAt).toISOString(),
    __failed: op.status === "failed", __error: op.status === "failed" ? op.lastError : null, __opId: op.operationId };
  if (op.type === "form") {
    const f = Object.fromEntries((op.fields || []).filter(([, v]) => typeof v === "string"));
    let localUrl = null;
    try {
      const fileEntry = (op.fields || []).find(([, v]) => v && typeof v !== "string");
      if (fileEntry) localUrl = URL.createObjectURL(reviveField(fileEntry[1]));
    } catch (_) {}
    return { ...base, ...f, category: f.category || "defect_photo", is_deleted: false, __localUrl: localUrl, name: f.name || f.title || "Offline-Foto" };
  }
  return { ...base, ...safeBody(op) };
}

async function overlayList(type, parent, arr) {
  let out = Array.isArray(arr) ? [...arr] : [];
  const ops = await allOps();
  for (const op of ops) {
    if (op.entityType !== type) continue;
    if (op.op === "delete") out = out.filter((x) => x.id !== op.entityId);
    else if (op.op === "update") out = out.map((x) => (x.id === op.entityId ? { ...x, ...overlayBody(op), __pending: true } : x));
    else if (op.op === "create" && op.parentId === parent) {
      if (!out.some((x) => x.id === op.entityId)) out.push(buildCreateItem(op));
    }
  }
  return out;
}

// Photos page (/media): prepend queued offline photo uploads so they show before syncing.
async function overlayMedia(arr) {
  const out = Array.isArray(arr) ? [...arr] : [];
  const ops = await allOps();
  const pending = ops
    .filter((op) => op.op === "create" && op.type === "form" && (op.fields || []).some(([, v]) => isImageField(v)))
    .filter((op) => !out.some((x) => x.id === op.entityId))
    .sort((a, b) => b.createdAt - a.createdAt)
    .map(buildCreateItem);
  return [...pending, ...out];
}

// Global Mängel/Tickets lists (/search): add queued offline creates/edits so they are visible with a pending badge.
async function overlaySearch(data) {
  if (!data || typeof data !== "object") return data;
  const ops = await allOps();
  if (!ops.some((o) => o.entityType === "defects" || o.entityType === "tickets")) return data;
  const cache = await idb.all("cache");
  const positions = cache.filter((e) => /^\/plans\/[^/]+\/positions$/.test(e?.key || "") && Array.isArray(e.data)).flatMap((e) => e.data);
  const projects = (cache.find((e) => e?.key === "g:/projects")?.data) || [];
  const out = { ...data };
  for (const k of ["defects", "tickets"]) {
    if (!Array.isArray(out[k])) continue;
    let arr = [...out[k]];
    for (const op of ops) {
      if (op.entityType !== k) continue;
      if (op.op === "delete") arr = arr.filter((x) => x.id !== op.entityId);
      else if (op.op === "update") arr = arr.map((x) => (x.id === op.entityId ? { ...x, ...overlayBody(op), __pending: true } : x));
      else if (op.op === "create" && !arr.some((x) => x.id === op.entityId)) {
        const it = buildCreateItem(op);
        const pos = positions.find((p) => p && p.id === (it.position_id || op.parentId));
        const projectId = it.project_id || pos?.project_id;
        arr.unshift({ ...it, position_id: it.position_id || op.parentId, plan_id: it.plan_id || pos?.plan_id, position_number: pos?.number,
          project_id: projectId, project_name: it.project_name || (Array.isArray(projects) ? projects.find((p) => p.id === projectId)?.name : undefined), status: it.status || "offen" });
      }
    }
    out[k] = arr;
  }
  return out;
}

const cacheKey = (url) => normPath(url);
// Generic GET cache key incl. axios params (sorted), so e.g. /tasks?scope=team and ?scope=mine don't share one entry.
const fullKey = (url, params) => {
  const base = "g:" + (url || "").split("#")[0];
  const ps = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== "").sort(([a], [b]) => a.localeCompare(b));
  if (!ps.length) return base;
  return base + (base.includes("?") ? "&" : "?") + ps.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
};
// Offline: exact query not cached yet -> closest prefetched variant (task list per scope, else the unfiltered list).
async function closestCached(cfg) {
  const exact = await idb.get("cache", fullKey(cfg.url, cfg.params));
  if (exact || !cfg.params) return exact;
  if (normPath(cfg.url) === "/tasks") {
    const s = await idb.get("cache", fullKey(cfg.url, { scope: cfg.params.scope || "mine", page_size: 200 }));
    if (s) return s;
  }
  return idb.get("cache", fullKey(cfg.url));
}
const binKey = (path) => "bin:" + path;
// Binary copies (plan files) as ArrayBuffer – iOS/WebKit can't reliably keep Blobs in IndexedDB.
export async function saveBin(path, blob) {
  try {
    await idb.put("cache", { key: binKey(path), data: { buf: await blob.arrayBuffer(), type: blob.type || "" }, ts: Date.now() });
    return true;
  } catch (_) { return false; }
}
export async function loadBin(path) {
  try {
    const e = await idb.get("cache", binKey(path));
    return e && e.data && e.data.buf ? new Blob([e.data.buf], { type: e.data.type }) : null;
  } catch (_) { return null; }
}
export async function hasBin(path) {
  try { const e = await idb.get("cache", binKey(path)); return !!(e && e.data && e.data.buf); } catch (_) { return false; }
}
export const AUTH_CACHE_KEY = "__auth_me";
export async function clearAuthCache() {
  try { await idb.del("cache", AUTH_CACHE_KEY); } catch (_) {}
}

// Durably enqueue a media upload (FormData) so the background sync retries it
// until it succeeds. Used when an online upload hits a transient (5xx/network)
// error or an unshrinkable payload — guarantees no photo is ever lost.
export async function queueMediaUpload(path, formData) {
  const fields = await serializeFields([...formData.entries()]);
  const f = Object.fromEntries(fields.filter(([, v]) => typeof v === "string"));
  let entityType = "files";
  let parentId = f.position_id || null;
  if (f.defect_id) { entityType = "photos"; parentId = f.defect_id; }
  const operationId = uid();
  const op = {
    operationId, idempotencyKey: operationId,
    method: "post", url: normPath(path),
    entityType, parentId, entityId: operationId,
    op: "create", createdAt: Date.now(), updatedAt: Date.now(), retryCount: 0, status: "pending",
    type: "form", fields,
  };
  await addOp(op);
  const pending = (await allOps()).length;
  setSyncState({ status: navigator.onLine ? SYNC.ONLINE : SYNC.OFFLINE, pending });
  window.dispatchEvent(new Event("offline-queued"));
  return { __queued: true };
}

api.interceptors.response.use(
  async (r) => {
    try {
      if ((r.config.method || "").toLowerCase() === "get" && r.config.responseType === "blob") {
        // Plan files: keep an iOS-safe copy (ArrayBuffer) in IndexedDB for offline use.
        if (/^\/plans\/[^/]+\/file$/.test(normPath(r.config.url)) && r.data instanceof Blob) await saveBin(normPath(r.config.url), r.data);
        return r;
      }
      if ((r.config.method || "").toLowerCase() === "get") {
        // Generic read-cache for offline (JSON responses only; billing stays live-only).
        if (!normPath(r.config.url).startsWith("/billing/")) try { await idb.put("cache", { key: fullKey(r.config.url, r.config.params), data: r.data, ts: Date.now() }); } catch (_) {}
        if (normPath(r.config.url) === "/auth/me" && r.data && typeof r.data === "object") {
          await idb.put("cache", { key: AUTH_CACHE_KEY, data: r.data, ts: Date.now() });
        }
        const list = classifyList(r.config.url);
        if (list && Array.isArray(r.data)) {
          await idb.put("cache", { key: cacheKey(r.config.url), data: r.data, ts: Date.now() });
          r.data = await overlayList(list.type, list.parent, r.data);
        }
        if (normPath(r.config.url) === "/media" && Array.isArray(r.data)) r.data = await overlayMedia(r.data);
        if (normPath(r.config.url) === "/search") r.data = await overlaySearch(r.data);
      }
    } catch (_) {}
    return r;
  },
  async (error) => {
    const cfg = error.config || {};
    const method = (cfg.method || "").toLowerCase();
    const isNetwork = !error.response;
    const status = error.response && error.response.status;

    // Expired access token -> refresh once in the background and replay the request.
    if (status === 401 && !cfg.__retried && !normPath(cfg.url).startsWith("/auth/")) {
      cfg.__retried = true;
      try {
        await runRefresh();
        return api(cfg);
      } catch (_) {
        window.dispatchEvent(new Event("session-expired"));
        return Promise.reject(error);
      }
    }

    // Offline plan file -> serve the stored bytes.
    if (method === "get" && isNetwork && cfg.responseType === "blob") {
      const b = await loadBin(normPath(cfg.url));
      if (b) return { data: b, status: 200, statusText: "OfflineCache", headers: {}, config: cfg, __offline: true };
    }

    // Offline cold-start: serve the last authenticated user so the app stays usable.
    if (method === "get" && isNetwork && normPath(cfg.url) === "/auth/me") {
      try {
        const cachedMe = await idb.get("cache", AUTH_CACHE_KEY);
        if (cachedMe && cachedMe.data) return { data: cachedMe.data, status: 200, statusText: "OfflineCache", headers: {}, config: cfg, __offline: true };
      } catch (_) {}
    }

    // Offline (or a stale temp-id 404) GET -> serve last-known payload + pending overlay.
    const status404 = error.response && error.response.status === 404;
    if (method === "get" && (isNetwork || status404)) {
      const pm = normPath(cfg.url).match(/^\/positions\/([^/]+)$/);
      if (pm && (isNetwork || pm[1].startsWith("op_"))) {
        try {
          const ops = await allOps();
          const co = ops.find((o) => o.op === "create" && o.entityType === "positions" && o.entityId === pm[1]);
          if (co) return { data: buildCreateItem(co), status: 200, statusText: "OfflineCache", headers: {}, config: cfg, __offline: true };
          if (pm[1].startsWith("op_")) return { data: { id: pm[1], __gone: true }, status: 200, statusText: "OfflineCache", headers: {}, config: cfg, __offline: true };
          if (isNetwork) {
            const own = await idb.get("cache", fullKey(cfg.url, cfg.params));
            if (own && own.data) return { data: own.data, status: 200, statusText: "OfflineCache", headers: {}, config: cfg, __offline: true };
            for (const entry of await idb.all("cache")) {
              if (!Array.isArray(entry?.data) || !/^\/plans\/[^/]+\/positions$/.test(entry.key || "")) continue;
              const hit = entry.data.find((x) => x && x.id === pm[1]);
              if (hit) return { data: hit, status: 200, statusText: "OfflineCache", headers: {}, config: cfg, __offline: true };
            }
          }
        } catch (_) {}
      }
      const list = classifyList(cfg.url);
      if (list && (isNetwork || (list.parent || "").startsWith("op_"))) {
        try {
          const cached = await idb.get("cache", cacheKey(cfg.url));
          const data = await overlayList(list.type, list.parent, cached ? cached.data : []);
          return { data, status: 200, statusText: "OfflineCache", headers: {}, config: cfg, __offline: true };
        } catch (_) {}
      }
    }

    // Generic offline read fallback for any cached GET (projects, dashboard, detail pages…).
    if (method === "get" && isNetwork && !normPath(cfg.url).startsWith("/billing/")) {
      try {
        const g = await closestCached(cfg);
        if (normPath(cfg.url) === "/media") return { data: await overlayMedia(g && Array.isArray(g.data) ? g.data : []), status: 200, statusText: "OfflineCache", headers: {}, config: cfg, __offline: true };
        if (g && g.data !== undefined && normPath(cfg.url) === "/search") return { data: await overlaySearch(g.data), status: 200, statusText: "OfflineCache", headers: {}, config: cfg, __offline: true };
        if (g && g.data !== undefined) return { data: g.data, status: 200, statusText: "OfflineCache", headers: {}, config: cfg, __offline: true };
      } catch (_) {}
    }

    // Offline write -> persist to queue + return an optimistic result immediately.
    if (isNetwork && ["post", "put", "delete"].includes(method)) {
      const c = classifyWrite(method, cfg.url);
      if (c) {
        if (c.op === "create" && c.type === "positions" && !c.parent) {
          try { c.parent = (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data || {}).plan_id || null; } catch (_) {}
        }
        if (c.op === "create" && c.type === "__media__") {
          let fields = [];
          try { if (cfg.data instanceof FormData) fields = [...cfg.data.entries()]; } catch (_) {}
          const f = Object.fromEntries(fields.filter(([, v]) => typeof v === "string"));
          if (f.defect_id) { c.type = "photos"; c.parent = f.defect_id; }
          else { c.type = "files"; c.parent = f.position_id || null; }
        }
        try {
          // Queue compaction: fold an update/delete of a still-pending offline CREATE
          // into that create (or cancel it) so we never replay against a temp id (404).
          if (c.op === "update" || c.op === "delete") {
            const existing = await allOps();
            const createOp = existing.find((o) => o.op === "create" && o.entityType === c.type && o.entityId === c.id);
            if (createOp) {
              const bodyStr = typeof cfg.data === "string" ? cfg.data : JSON.stringify(cfg.data || {});
              if (c.op === "delete") {
                await removeOp(createOp.operationId);
                for (const o of existing) {
                  if (o.op === "update" && o.entityType === c.type && o.entityId === c.id) await removeOp(o.operationId);
                }
              } else if (createOp.type !== "form") {
                let merged = {};
                try { merged = { ...JSON.parse(createOp.body || "{}"), ...safeBody({ body: bodyStr }) }; } catch (_) {}
                await updateOp({ ...createOp, body: JSON.stringify(merged), updatedAt: Date.now() });
              }
              const pending2 = (await allOps()).length;
              setSyncState({ status: SYNC.OFFLINE, pending: pending2 });
              window.dispatchEvent(new Event("offline-queued"));
              const optimistic2 = c.op === "delete" ? { id: c.id } : safeBody({ body: bodyStr });
              return { data: optimistic2, status: 202, statusText: "Queued", headers: {}, config: cfg, __queued: true };
            }
          }
          const operationId = uid();
          const op = {
            operationId, idempotencyKey: operationId,
            method, url: normPath(cfg.url),
            entityType: c.type, parentId: c.parent || null,
            entityId: c.op === "create" ? operationId : c.id,
            op: c.op, createdAt: Date.now(), updatedAt: Date.now(), retryCount: 0, status: "pending",
          };
          if (typeof FormData !== "undefined" && cfg.data instanceof FormData) {
            op.type = "form"; op.fields = await serializeFields([...cfg.data.entries()]);
          } else {
            op.type = "json"; op.body = typeof cfg.data === "string" ? cfg.data : JSON.stringify(cfg.data || {});
          }
          await addOp(op);
          const pending = (await allOps()).length;
          setSyncState({ status: SYNC.OFFLINE, pending });
          window.dispatchEvent(new Event("offline-queued"));
          const optimistic = c.op === "create" ? buildCreateItem(op) : (c.op === "update" ? safeBody(op) : { id: c.id });
          return { data: optimistic, status: 202, statusText: "Queued", headers: {}, config: cfg, __queued: true };
        } catch (_) {
          return Promise.reject(error);
        }
      }
    }
    return Promise.reject(error);
  },
);

export default api;
